import HologardeBackground from '@assets/hologarde-background.jpg';
import { IcUser } from '@assets/icons/24x24';
import {
  Box,
  Button,
  Center,
  Field as ChakraField,
  Fieldset,
  Flex,
  Icon,
  Image,
  Input,
  InputGroup,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Form } from '@components/ui/Form';
import { PasswordInput } from '@components/ui/password-input';
import useValidation from '@hooks/useValidation';
import { LoginRequest, useLoginMutation } from '@services/authent/login.api';
import { createErrorMessage } from '@utils/common.utils';
import { loginSchema } from '@validations/login.schema';
import { Field, FieldInputProps, Formik, FormikProps } from 'formik';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ErrorsContainer from '../common/form/ErrorsContainer';

export default function Login() {
  const validationSchema = useValidation(loginSchema);
  const { formatMessage } = useIntl();

  const [login, { isLoading, isError, error }] = useLoginMutation();

  const handleLogin = useCallback(
    async (values: LoginRequest) => {
      await login(values);
    },
    [login],
  );

  return (
    <Flex width="100%" height="100%" userSelect="none">
      <Image src={HologardeBackground} fit="cover" width="100%" height="100%" position="absolute" />
      <Center
        height="full"
        width="424px"
        position="fixed"
        right={0}
        padding={8}
        zIndex={3}
        backgroundColor="neutral.800-op70"
      >
        <Formik
          initialValues={{
            login: '',
            password: '',
          }}
          onSubmit={handleLogin}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
        >
          {({ handleSubmit, errors, touched, isValid, dirty }) => (
            <Fieldset.Root
              invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched]) || isError}
              justifyContent="center"
            >
              <Box>
                <Fieldset.Legend alignItems="center">
                  <Text fontSize="32px" letterSpacing="normal" fontFamily="space">
                    <FormattedMessage id="authent.title" />
                  </Text>
                </Fieldset.Legend>
              </Box>

              <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Fieldset.Content>
                  <ChakraField.Root invalid={!!(touched.login && errors.login)} required>
                    <ChakraField.Label>
                      <FormattedMessage id="authent.user.login" />
                    </ChakraField.Label>
                    <Field name="login">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<{ name: string; surname: string }>;
                      }) => (
                        <InputGroup
                          startElement={<Icon as={IcUser} color="neutral.500" />}
                          startElementProps={{ paddingInline: 2 }}
                        >
                          <Input
                            {...field}
                            type="text"
                            style={{ paddingInlineStart: '52px' }}
                            placeholder={formatMessage({ id: 'authent.user.login' })}
                            onChange={form.handleChange}
                            onBlur={form.handleBlur}
                          />
                        </InputGroup>
                      )}
                    </Field>
                  </ChakraField.Root>
                  <ChakraField.Root invalid={!!(touched.password && errors.password)} required>
                    <ChakraField.Label>
                      <FormattedMessage id="authent.user.password" />
                    </ChakraField.Label>
                    <Field name="password">
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<string>;
                        form: FormikProps<{ name: string; surname: string }>;
                      }) => (
                        <PasswordInput {...field} showLockIcon onChange={form.handleChange} onBlur={form.handleBlur} />
                      )}
                    </Field>
                  </ChakraField.Root>
                </Fieldset.Content>
                <Fieldset.ErrorText>
                  <ErrorsContainer
                    touched={touched}
                    errors={errors}
                    apiError={isError ? createErrorMessage(error) : undefined}
                  />
                </Fieldset.ErrorText>
                <Box marginTop={3} width="100%">
                  <Button
                    type="submit"
                    width="100%"
                    size="md"
                    loading={isLoading}
                    spinner={<Spinner size="lg" color="neutral.white" />}
                    disabled={!isValid || !dirty}
                  >
                    <FormattedMessage id="authent.connection" />
                  </Button>
                </Box>
              </Form>
              <Fieldset.HelperText>
                <Text>
                  <FormattedMessage
                    id="authent.help"
                    values={{ number: formatMessage({ id: 'global.adminNumber' }) }}
                  />
                </Text>
              </Fieldset.HelperText>
            </Fieldset.Root>
          )}
        </Formik>
      </Center>
    </Flex>
  );
}
